import {
    type ShippingOrderCargoModel,
    ShippingOrderFlags,
    type ShippingOrderListModel,
    type ShippingOrderModel,
    ShippingOrderStatus,
    ShippingOrderTitleStatus,
    ShippingOrderTitleType,
} from "api";

export const shippingOrderRejectionReasonMaxLength = 2000;

/** Sum cargo mass from the backend kilogram field. */
export const getShippingOrderCargoTotalWeightKg = (
    cargos: ReadonlyArray<Pick<ShippingOrderCargoModel, "weight">>,
) => cargos.reduce((total, { weight }) => total + (Number(weight) || 0), 0);

export const shippingOrderRegistryStatuses = [
    {
        label: "Т1 создан",
        value: "T1Created",
        titleType: ShippingOrderTitleType.T1,
        titleStatus: ShippingOrderTitleStatus.ReceivedOrCreated,
        requiresSignature: false,
    },
    {
        label: "Т1 подписан",
        value: "T1Signed",
        titleType: ShippingOrderTitleType.T1,
        titleStatus: ShippingOrderTitleStatus.Signed,
        requiresSignature: false,
    },
    {
        label: "Т2 создан",
        value: "T2Created",
        titleType: ShippingOrderTitleType.T2,
        titleStatus: ShippingOrderTitleStatus.ReceivedOrCreated,
        requiresSignature: true,
    },
    {
        label: "Т2 подписан",
        value: "T2Signed",
        titleType: ShippingOrderTitleType.T2,
        titleStatus: ShippingOrderTitleStatus.Signed,
        requiresSignature: false,
    },
] as const;

type ResponseTitleState = Pick<ShippingOrderModel, "status" | "titles">;

/** Resolve the last-title label, including legacy incoming T1 records. */
export const getShippingOrderRegistryStatusLabel = (
    shippingOrder: Pick<ShippingOrderListModel, "lastTitleType" | "lastTitleStatus">,
) =>
    shippingOrderRegistryStatuses.find(
        ({ titleType, titleStatus }) =>
            titleType === shippingOrder.lastTitleType && titleStatus === shippingOrder.lastTitleStatus,
    )?.label || "Статус не определён";

/** Check the decline bit while preserving unrelated flags. */
export const isShippingOrderDeclined = (shippingOrder: { intFlags?: number }) =>
    ((shippingOrder.intFlags ?? 0) & ShippingOrderFlags.IsDecline) === ShippingOrderFlags.IsDecline;

/** Find the response title regardless of the system in which it was signed. */
export const getShippingOrderResponseTitle = (shippingOrder: Pick<ShippingOrderModel, "titles">) =>
    shippingOrder.titles?.find((title) => title.titleType === ShippingOrderTitleType.T2);

/** Recognize a signed response from its title or confirmed server status. */
export const isShippingOrderResponseTitleSigned = (shippingOrder: ResponseTitleState) =>
    getShippingOrderResponseTitle(shippingOrder)?.status === ShippingOrderTitleStatus.Signed ||
    shippingOrder.status === ShippingOrderStatus.ResponseTitleSent ||
    shippingOrder.status === ShippingOrderStatus.Completed;

/** Restrict signing and retry actions by permissions and the server lifecycle. */
export const canSignShippingOrderResponseTitle = (shippingOrder: ResponseTitleState, canSign: boolean) =>
    canSign &&
    shippingOrder.status !== ShippingOrderStatus.ResponseTitleSent &&
    shippingOrder.status !== ShippingOrderStatus.Completed &&
    (!isShippingOrderResponseTitleSigned(shippingOrder) ||
        shippingOrder.status === ShippingOrderStatus.FailedToSendResponseTitle);

/** Restrict declining by permissions, existing decline state and server lifecycle. */
export const canDeclineShippingOrderResponseTitle = (
    shippingOrder: ResponseTitleState & { intFlags?: number },
    canSign: boolean,
) =>
    canSign &&
    !isShippingOrderDeclined(shippingOrder) &&
    shippingOrder.status !== ShippingOrderStatus.FailedToSendResponseTitle &&
    shippingOrder.status !== ShippingOrderStatus.ResponseTitleSent &&
    shippingOrder.status !== ShippingOrderStatus.Completed &&
    !isShippingOrderResponseTitleSigned(shippingOrder);

/** Validate the mandatory decline reason before generating a response title. */
export const validateShippingOrderRejectionReason = (value: string) => {
    if (!value.trim()) {
        return "Укажите причину отказа";
    }
    if (value.length > shippingOrderRejectionReasonMaxLength) {
        return `Причина отказа должна содержать не более ${shippingOrderRejectionReasonMaxLength} символов`;
    }
    return null;
};
